import { Button, Checkbox, Dialog, MobileDrawer } from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useCallback, useEffect, useState } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export interface ResolveTicketDialogProps {
  isOpen: boolean;
  ticketUniqueId: string;
  onClose: () => void;
  onConfirm: (options: { skipFutureAlerts: boolean }) => void;
}

export const ResolveTicketDialog: FC<ResolveTicketDialogProps> = ({
  isOpen,
  ticketUniqueId,
  onClose,
  onConfirm,
}) => {
  const [skipFutureAlerts, setSkipFutureAlerts] = useState(false);
  const { isMobile } = useAppVariant();
  const { t } = useTranslations();

  const handleConfirm = useCallback(() => {
    onConfirm({ skipFutureAlerts });
    onClose();
  }, [onClose, onConfirm, skipFutureAlerts]);

  useEffect(() => {
    if (isOpen) {
      setSkipFutureAlerts(false);
    }
  }, [isOpen]);

  const title = t({
    id: "client-support-page.resolve-ticket-emoji",
    defaultMessage: "🎟️ Resolve Ticket",
  });

  const description = t(
    {
      id: "client-support-page.resolve-ticket-description",
      defaultMessage:
        "Are you sure you want to resolve {ticketId} ticket? It can be reopened any time",
    },
    {
      ticketId: (
        <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
          #{ticketUniqueId}
        </span>
      ),
    }
  );

  const dontAskAgainLabel = t({
    id: "common.dont-ask-me-again",
    defaultMessage: "Don't ask me again",
  });

  const cancelLabel = t({
    id: "common.cancel",
    defaultMessage: "Cancel",
  });

  const resolveLabel = t({
    id: "client-support-page.resolve",
    defaultMessage: "Resolve",
  });

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        size="auto"
        header={{ title, hasCloseButton: false }}
        onClose={onClose}
      >
        <MobileDrawer.Content>
          <span className="font-secondary text-sm leading-[21px] text-dark-700">
            {description}
          </span>
          <div className="mt-[18px]">
            <Checkbox
              label={dontAskAgainLabel}
              labelClassName="font-secondary text-[13px] text-dark-900 leading-[150%]"
              isChecked={skipFutureAlerts}
              onChange={(event) => setSkipFutureAlerts(event.target.checked)}
            />
          </div>
        </MobileDrawer.Content>
        <MobileDrawer.Actions isHorizontal className="gap-4 pb-0">
          <Button variant="tertiary" onClick={onClose} className="flex-1">
            {cancelLabel}
          </Button>
          <Button variant="primary" onClick={handleConfirm} className="flex-1">
            {resolveLabel}
          </Button>
        </MobileDrawer.Actions>
      </MobileDrawer>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-[480px] flex-col overflow-hidden rounded-2xl p-10"
    >
      <span className="font-secondary text-[22px] font-bold text-dark">
        {title}
      </span>
      <span className="mt-2.5 font-secondary text-sm leading-[21px] text-dark-700">
        {description}
      </span>
      <div className="mt-[18px]">
        <Checkbox
          label={dontAskAgainLabel}
          labelClassName="font-secondary text-[13px] text-dark-900 leading-[150%]"
          isChecked={skipFutureAlerts}
          onChange={(event) => setSkipFutureAlerts(event.target.checked)}
        />
      </div>
      <div className="mt-14 flex items-center gap-4">
        <Button onClick={onClose} variant="tertiary" className="w-[140px]">
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          iconEnd={<ArrowRightIcon />}
          className="flex-1"
        >
          {resolveLabel}
        </Button>
      </div>
    </Dialog>
  );
};
